import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaTrophy, 
  FaFire, 
  FaBell, 
  FaChartLine, 
  FaStar, 
  FaHeart 
} from 'react-icons/fa';
import './Features.css';

const Features = () => {
  const features = [
    {
      icon: <FaTrophy />,
      title: "Level Up System",
      description: "Earn XP points for completing routines and unlock new levels. Watch your skincare dedication transform into tangible achievements.",
      color: "#ffd700"
    },
    {
      icon: <FaFire />,
      title: "Streak Tracking",
      description: "Build momentum with daily streak counters. Maintain consistency and watch your fire streak grow stronger each day.",
      color: "#ff6b35"
    },
    {
      icon: <FaBell />,
      title: "Smart Reminders",
      description: "Never miss your skincare routine with personalized notifications. Set custom times for morning and evening routines.",
      color: "#ff9a9e"
    },
    {
      icon: <FaChartLine />,
      title: "Progress Analytics",
      description: "Track your skincare journey with detailed progress charts. See how consistency leads to better skin health over time.",
      color: "#4ecdc4"
    },
    {
      icon: <FaStar />,
      title: "Achievement Badges",
      description: "Unlock special badges for milestones like 'Glow Getter' and 'Dew Master'. Collect them all and show off your dedication.",
      color: "#fecfef"
    },
    {
      icon: <FaHeart />,
      title: "Personalized Routines",
      description: "Create custom morning and evening routines tailored to your skin type. Add products, set timers, and track completion.",
      color: "#ff9ff3"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="features" className="features section">
      <div className="container">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Level Up Your Skincare</h2>
          <p className="section-subtitle">
            Gamified features designed to make your skincare routine engaging and rewarding
          </p>
        </motion.div>

        <motion.div 
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
            >
              <div 
                className="feature-icon"
                style={{ backgroundColor: feature.color }}
              >
                {feature.icon}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          className="features-highlight"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="highlight-content">
            <div className="highlight-text">
              <h3>Perfect for Every Skincare Enthusiast</h3>
              <ul className="traveler-types">
                <li>🌟 Beginners building their first skincare routine</li>
                <li>💎 Skincare veterans looking to stay consistent</li>
                <li>🎯 Goal-oriented people who love tracking progress</li>
                <li>🏆 Competitive spirits who enjoy achievements</li>
                <li>✨ Anyone wanting to turn self-care into a fun habit</li>
              </ul>
            </div>
            <div className="highlight-visual">
              <div className="countdown-preview">
                <div className="preview-card">
                  <div className="preview-image gradient-bg-1"></div>
                  <div className="preview-info">
                    <span className="preview-destination">Level 15</span>
                    <span className="preview-countdown">Glow Master</span>
                  </div>
                </div>
                <div className="preview-card">
                  <div className="preview-image gradient-bg-2"></div>
                  <div className="preview-info">
                    <span className="preview-destination">30 Day</span>
                    <span className="preview-countdown">Streak 🔥</span>
                  </div>
                </div>
                <div className="preview-card">
                  <div className="preview-image gradient-bg-3"></div>
                  <div className="preview-info">
                    <span className="preview-destination">Achievement</span>
                    <span className="preview-countdown">Unlocked ⭐</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
